import { lazy, type ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { RootLayout } from "./app/root-layout";
import { RootErrorBoundary } from "./app/error-boundary";
import { ProtectedRoute } from "./lib/protected-route";
import { AuthLayout } from "./features/auth/auth-layout";
import { LoginPage } from "./features/auth/login-page";
import { ForgotPasswordPage } from "./features/auth/forgot-password-page";
import { ResetPasswordPage } from "./features/auth/reset-password-page";
import { InvitePage } from "./features/auth/invite-page";
import { AuthCallbackPage } from "./features/auth/callback-page";
import { RoleRedirect } from "./features/auth/role-redirect";
import { AdminLayout } from "./features/admin/admin-layout";

// FE1: the entire admin section is lazily code-split so partners never
// download it (it pulls section-admin + the dnd-kit builders). Each page is a
// named export, mapped to a default-shaped module for React.lazy; a Suspense
// boundary in AdminLayout/PortalLayout shows the RouteFallback while a chunk
// loads. Keep this list in sync with the admin routes below.
function page(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
) {
  return lazy(() =>
    loader().then((m) => ({ default: m[name] as ComponentType })),
  );
}

const AdminDashboardPage = page(() => import("./features/admin/dashboard-page"), "AdminDashboardPage");
const PartnersListPage = page(() => import("./features/admin/partners/partners-list-page"), "PartnersListPage");
const PartnerDetailPage = page(() => import("./features/admin/partners/partner-detail-page"), "PartnerDetailPage");
const FormsListPage = page(() => import("./features/admin/forms/forms-list-page"), "FormsListPage");
const FormBuilderPage = page(() => import("./features/admin/forms/form-builder-page"), "FormBuilderPage");
const TierProgramsListPage = page(() => import("./features/admin/tiers/tier-programs-list-page"), "TierProgramsListPage");
const TierProgramEditorPage = page(() => import("./features/admin/tiers/tier-program-editor-page"), "TierProgramEditorPage");
const ContentListPage = page(() => import("./features/admin/content/content-list-page"), "ContentListPage");
const AssetDetailPage = page(() => import("./features/admin/content/asset-detail-page"), "AssetDetailPage");
const AdminJourneysListPage = page(() => import("./features/admin/journeys/journeys-list-page"), "JourneysListPage");
const JourneyBuilderPage = page(() => import("./features/admin/journeys/journey-builder-page"), "JourneyBuilderPage");
const MdfFundsListPage = page(() => import("./features/admin/mdf/funds-list-page"), "MdfFundsListPage");
const MdfFundDetailPage = page(() => import("./features/admin/mdf/fund-detail-page"), "MdfFundDetailPage");
const MdfRequestsQueuePage = page(() => import("./features/admin/mdf/requests-queue-page"), "MdfRequestsQueuePage");
const AdminDealsListPage = page(() => import("./features/admin/deals/deals-list-page"), "DealsListPage");
const AdminSubmissionsQueuePage = page(() => import("./features/admin/submissions/submissions-queue-page"), "SubmissionsQueuePage");
const AdminPendingLeadsQueuePage = page(() => import("./features/admin/pending-leads/pending-leads-queue-page"), "PendingLeadsQueuePage");
const SettingsOverviewPage = page(() => import("./features/admin/settings/settings-overview-page"), "SettingsOverviewPage");
const IntegrationsListPage = page(() => import("./features/admin/settings/integrations-list-page"), "IntegrationsListPage");
const IntegrationsHubspotPage = page(() => import("./features/admin/settings/integrations-hubspot-page"), "IntegrationsHubspotPage");
const RolesListPage = page(() => import("./features/admin/settings/roles-list-page"), "RolesListPage");
const RoleDetailPage = page(() => import("./features/admin/settings/role-detail-page"), "RoleDetailPage");
const SegmentsListPage = page(() => import("./features/admin/settings/segments-list-page"), "SegmentsListPage");
const SegmentDetailPage = page(() => import("./features/admin/settings/segment-detail-page"), "SegmentDetailPage");
const AdminNotificationsPage = page(() => import("./features/admin/settings/notifications-page"), "NotificationsPage");
const AdminAnalyticsPage = page(() => import("./features/admin/analytics/admin-analytics-page"), "AdminAnalyticsPage");
const PortalContentListPage = page(() => import("./features/admin/portal-content/portal-content-list-page"), "PortalContentListPage");
const FaqListPage = page(() => import("./features/admin/portal-content/faq-list-page"), "FaqListPage");
const GoalsListPage = page(() => import("./features/admin/goals/goals-list-page"), "GoalsListPage");
const GoalDetailPage = page(() => import("./features/admin/goals/goal-detail-page"), "GoalDetailPage");
const AdminConnectionsPage = page(() => import("./features/admin/settings/connections-page"), "AdminConnectionsPage");
const UsersPage = page(() => import("./features/admin/users/users-page"), "UsersPage");
const ListingChangesQueuePage = page(() => import("./features/admin/listing-changes/listing-changes-queue-page"), "ListingChangesQueuePage");
import { PortalLayout } from "./features/portal/portal-layout";
import { PortalDashboardPage } from "./features/portal/dashboard-page";
import { OnboardingPendingPage } from "./features/portal/onboarding-pending-page";
import { ProfilePage } from "./features/portal/profile/profile-page";
import { SettingsPage } from "./features/portal/settings/settings-page";
import { PortalErrorElement } from "./features/portal/portal-error-element";
import { PortalFormsListPage } from "./features/portal/forms/forms-list-page";
import { FormDetailPage } from "./features/portal/forms/form-detail-page";
import { FormSubmittedPage } from "./features/portal/forms/form-submitted-page";
import { MdfListPage } from "./features/portal/mdf/mdf-list-page";
import { MdfNewPage } from "./features/portal/mdf/mdf-new-page";
import { MdfDetailPage } from "./features/portal/mdf/mdf-detail-page";
import { JourneysListPage } from "./features/portal/journeys/journeys-list-page";
import { JourneyDetailPage } from "./features/portal/journeys/journey-detail-page";
import { DealsListPage } from "./features/portal/deals/deals-list-page";
import { DealNewPage } from "./features/portal/deals/deal-new-page";
import { DealDetailPage } from "./features/portal/deals/deal-detail-page";
// Partner analytics is lazy-split: it's the only partner page that pulls
// vendor-recharts, so keeping it out of the portal entry chunk is the win.
const AnalyticsPage = page(
  () => import("./features/portal/analytics/analytics-page"),
  "AnalyticsPage",
);
import { ResourcesPage } from "./features/portal/resources/resources-page";
import { OnboardingPage } from "./features/portal/onboarding/onboarding-page";
import { SupportPage } from "./features/portal/support/support-page";
import { ListingPage } from "./features/portal/listing/listing-page";
import { EmbedLayout } from "./features/embed/embed-layout";
import { EmbedFormPage } from "./features/embed/forms/embed-form-page";

// Route tree. Section-level <ProtectedRoute> wrappers replace the Next.js
// middleware role gate; <AuthLayout> / <AdminLayout> / <PortalLayout> /
// <EmbedLayout> replace the four legacy /app/(group)/layout.tsx shells.
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      // Root → role-based home (was middleware redirect).
      { index: true, element: <RoleRedirect /> },

      // Public auth surface — all four share the centered card shell.
      {
        element: <AuthLayout />,
        children: [
          { path: "login", element: <LoginPage /> },
          { path: "forgot-password", element: <ForgotPasswordPage /> },
          { path: "reset-password", element: <ResetPasswordPage /> },
          { path: "invite/:token", element: <InvitePage /> },
        ],
      },

      // Bare callback — no chrome, just resolves the session and redirects.
      { path: "auth/callback", element: <AuthCallbackPage /> },

      // Admin section — internal users only.
      {
        path: "admin",
        element: <ProtectedRoute role="internal" />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <Navigate to="/admin/dashboard" replace /> },
              { path: "dashboard", element: <AdminDashboardPage /> },
              // Unified user manager — internal team + partner contacts, with
              // role/permission + account-access controls. Gated on users.read
              // (sidebar) + users.manage (mutations, server-side).
              { path: "users", element: <UsersPage /> },
              // Phase 7 sub-bucket 10 — admin partners. List page mounts a
              // toolbar (search + status/type/tier chip filters), sortable
              // table, and the "Add partner" CreatePartnerSheet. Detail
              // page ships 4 tabs (overview / people / tasks / notes); the
              // analytics / deals / content / goals tabs land with later
              // admin sub-buckets that depend on admin-side endpoints.
              {
                path: "partners",
                children: [
                  { index: true, element: <PartnersListPage /> },
                  { path: ":id", element: <PartnerDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 11 — admin forms. List page mounts a
              // search + status chip filter + sortable table; builder page
              // ships the 3-tab editor (build / automation / workflow) with
              // @dnd-kit drag-drop, inline sticky field-config panel, and
              // optimistic per-field hooks. /:id/edit preserves the legacy
              // path shape so older bookmarks redirect cleanly post-cutover.
              {
                path: "forms",
                children: [
                  { index: true, element: <FormsListPage /> },
                  { path: ":id/edit", element: <FormBuilderPage /> },
                ],
              },
              // Phase 7 sub-bucket 12 — admin tier programs. List page mounts
              // a default/archived chips table with row-action menu; editor
              // page hosts three tabs (matrix / assignments / settings) keyed
              // by ?tab= URL state. First live consumer of useReorderTiers +
              // useReorderTierRows (both axes drag-reorderable) and second
              // user of the vendor-dnd-kit chunk.
              {
                path: "tiers",
                children: [
                  { index: true, element: <TierProgramsListPage /> },
                  { path: ":id", element: <TierProgramEditorPage /> },
                ],
              },
              // Phase 7 sub-bucket 13 — admin content library. List page
              // (with ?folder= folder scope, debounced ?q= search, ?type=
              // chip filter, ?archived=1 toggle, ?view=grid|list switch);
              // detail page (with ?tab=metadata|views, per-field autosave
              // for metadata fields, total + unique-partner + 30-day
              // sparkline + recent feed for views). Reuses the Phase 6
              // mutation hooks under apps/web/src/features/admin/content/;
              // second consumer of the upload-init → direct-PUT → patch
              // pattern (first was the tier-badge upload).
              {
                path: "content",
                children: [
                  { index: true, element: <ContentListPage /> },
                  { path: ":id", element: <AssetDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 14 — admin journeys (list + builder).
              // Two tabs on the builder (tasks / assignments) keyed by
              // ?tab=. Third consumer of vendor-dnd-kit (after forms +
              // tiers); also the first surface exposing the new info_*
              // admin-content action kinds added to journeyTaskActionSchema
              // in this sub-bucket.
              {
                path: "journeys",
                children: [
                  { index: true, element: <AdminJourneysListPage /> },
                  { path: ":id", element: <JourneyBuilderPage /> },
                ],
              },
              // Phase 7 sub-bucket 15 — admin MDF. Funds index + per-fund
              // detail (4 tabs: configuration / allocations / requests / roi)
              // + cross-fund requests queue (with status chip + fund dropdown
              // + search filters). The `/requests` literal sits alongside
              // `/:id` because requests is a meaningful first-class surface,
              // not nested under a single fund.
              {
                path: "mdf",
                children: [
                  { index: true, element: <MdfFundsListPage /> },
                  { path: "requests", element: <MdfRequestsQueuePage /> },
                  { path: ":id", element: <MdfFundDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 16 — admin deals (HubSpot pipeline
              // Kanban). The drawer is mounted at page level inside
              // <DealsListPage> as a drawer-as-shareable-route via
              // `?deal=<hubspotDealId>` — this convention is the template
              // sub-bucket 17 (submissions) will mirror with `?submission=`.
              { path: "deals", element: <AdminDealsListPage /> },
              // Phase 7 sub-bucket 17 — admin submissions queue. Adopts the
              // drawer-as-shareable-route convention with `?submission=<id>`
              // (sub-bucket 16 established the pattern with `?deal=`).
              // Saved Views deliberately deferred on this surface — the
              // `submission` catalog needs a stabilisation pass; the
              // mdf-requests page in this same sub-bucket is the live
              // Saved Views consumer.
              { path: "submissions", element: <AdminSubmissionsQueuePage /> },
              // Queued-leads queue (pending_leads dead-letter). Read-only list
              // + manual "Retry now" drain — gives staff visibility into
              // partner leads that haven't reached HubSpot yet.
              { path: "pending-leads", element: <AdminPendingLeadsQueuePage /> },
              // 0091 — marketplace listing-change review queue.
              { path: "listing-changes", element: <ListingChangesQueuePage /> },
              // Phase 7 sub-bucket 18 — admin analytics. Workspace-aggregate
              // dashboard (NOT a partner-scoped <PartnerAnalyticsView>
              // reuse — legacy /admin/analytics has always been
              // workspace-aggregate).
              { path: "analytics", element: <AdminAnalyticsPage /> },
              // Phase 7 sub-bucket 18 — admin portal-content CMS (welcome /
              // level_up / support_links + FAQ). Fifth consumer of
              // vendor-dnd-kit via the FAQ reorder surface.
              {
                path: "portal-content",
                children: [
                  { index: true, element: <PortalContentListPage /> },
                  { path: "faq", element: <FaqListPage /> },
                ],
              },
              // Phase 7 sub-bucket 18 — admin goals + targets editor.
              {
                path: "goals",
                children: [
                  { index: true, element: <GoalsListPage /> },
                  { path: ":id", element: <GoalDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 18 — admin settings (8 pages):
              //   /admin/settings                         landing (announcements + invites)
              //   /admin/settings/integrations            directory
              //   /admin/settings/integrations/hubspot    HubSpot configure
              //   /admin/settings/roles                   roles list
              //   /admin/settings/roles/:id               role permissions + members
              //   /admin/settings/segments                segments list (tabs)
              //   /admin/settings/segments/:id            segment editor (FilterTreeBuilder)
              //   /admin/settings/notifications           per-event rule board
              {
                path: "settings",
                children: [
                  { index: true, element: <SettingsOverviewPage /> },
                  {
                    path: "integrations",
                    children: [
                      { index: true, element: <IntegrationsListPage /> },
                      { path: "hubspot", element: <IntegrationsHubspotPage /> },
                    ],
                  },
                  {
                    path: "roles",
                    children: [
                      { index: true, element: <RolesListPage /> },
                      { path: ":id", element: <RoleDetailPage /> },
                    ],
                  },
                  {
                    path: "segments",
                    children: [
                      { index: true, element: <SegmentsListPage /> },
                      { path: ":id", element: <SegmentDetailPage /> },
                    ],
                  },
                  { path: "notifications", element: <AdminNotificationsPage /> },
                  { path: "connections", element: <AdminConnectionsPage /> },
                ],
              },
              // Admin sub-routes added in Phase 7.
            ],
          },
        ],
      },

      // Portal section — partners only (or internal in preview-as mode).
      {
        path: "portal",
        element: <ProtectedRoute role="partner" allowInternalPreviewAs />,
        children: [
          {
            element: <PortalLayout />,
            errorElement: <PortalErrorElement />,
            children: [
              { index: true, element: <Navigate to="/portal/dashboard" replace /> },
              { path: "dashboard", element: <PortalDashboardPage /> },
              // Onboarding-pending: referenced by ProtectedRoute when a
              // partner has no partnerId. Phase 7 fleshes out the page.
              { path: "onboarding-pending", element: <OnboardingPendingPage /> },
              // Phase 7 sub-bucket 7 — partner profile (field-level autosave)
              // and net-new settings page. The callback page routes
              // freshly-onboarded partners to /portal/profile?setup=1 — the
              // page renders an onboarding banner and strips the param on
              // first save.
              { path: "profile", element: <ProfilePage /> },
              { path: "settings", element: <SettingsPage /> },
              // Phase 7 sub-bucket 2 — Portal forms.
              {
                path: "forms",
                children: [
                  { index: true, element: <PortalFormsListPage /> },
                  { path: ":slug", element: <FormDetailPage /> },
                  { path: ":slug/submitted", element: <FormSubmittedPage /> },
                ],
              },
              // Phase 7 sub-bucket 3 — Portal MDF. Detail route is new (the
              // legacy used a Sheet drawer over the list); status='draft'
              // hosts the editable runtime view, all other statuses render
              // read-only with timeline + comments + attachment downloads.
              {
                path: "mdf",
                children: [
                  { index: true, element: <MdfListPage /> },
                  { path: "new", element: <MdfNewPage /> },
                  { path: ":id", element: <MdfDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 4 — Portal journeys. List page with
              // active/completed segmented tabs; detail page with the flat
              // ordered task list discriminating on action.kind (none /
              // note / link / attachment / form_submission). Snooze +
              // proof + comments + attachments expand the data model
              // beyond legacy parity (see migration 0069, sub-bucket 4a).
              {
                path: "journeys",
                children: [
                  { index: true, element: <JourneysListPage /> },
                  { path: ":assignmentId", element: <JourneyDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 5 — Portal deals. Rich rebuild beyond the
              // legacy single-list page per user decision: unified `deals` +
              // `hubspot_deal_attributions` feed, status-aware detail page
              // (edit-in-place for submitted leads vs read-only for
              // attribution rows), submit-a-lead route, comment thread (re-
              // surfacing bucket-3 dead code), status timeline derived from
              // deals.status_history, HubSpot owner chip. Inbound vs
              // outbound separation is the bucket's signature constraint.
              {
                path: "deals",
                children: [
                  { index: true, element: <DealsListPage /> },
                  { path: "new", element: <DealNewPage /> },
                  { path: ":id", element: <DealDetailPage /> },
                ],
              },
              // Phase 7 sub-bucket 6 — Portal analytics. Single-route page
              // with `?range=` URL state (7d/30d/90d/all). Hosts the shared
              // <PartnerAnalyticsView> composition that admin sub-bucket 10
              // will reuse for the admin partner-detail analytics tab.
              // EUR-only ARR filter is enforced at the service layer
              // (apps/api/src/services/analytics/queries.ts), not here.
              { path: "analytics", element: <AnalyticsPage /> },
              // Phase 7 sub-bucket 8 — portal close-out. Resources library
              // (with optional `?folder=<id>` deep-link), onboarding task
              // list (Pattern C optimistic toggle), and read-only support
              // page reusing the dashboard FaqAccordion. No new admin/embed
              // surfaces — every partner-visible legacy route now lives in
              // apps/web/src/features/portal/.
              { path: "resources", element: <ResourcesPage /> },
              { path: "onboarding", element: <OnboardingPage /> },
              { path: "support", element: <SupportPage /> },
              // 0091 — marketplace "My Listing". The nav item self-hides for
              // unlinked partners; the page itself renders a quiet empty
              // state when reached by URL.
              { path: "listing", element: <ListingPage /> },
            ],
          },
        ],
      },

      // Embed surface — public, iframable from third-party origins. CSP for
      // /embed/* lives in apps/web/public/_headers (frame-ancestors *).
      {
        path: "embed",
        element: <EmbedLayout />,
        children: [{ path: "forms/:slug", element: <EmbedFormPage /> }],
      },

      // 404.
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
